import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@mui/material';
import { COLORS } from '../../../../theme/themeConstants';
import * as Yup from 'yup';
import allForm from '../../../Stateless/Form';
import { toast } from 'react-hot-toast';
import {
  transformFormDataToSchema,
  GeneralSettingsSchema,
} from './GeneralSettingsSchema';
import { useSettingsCreate } from '../../../Query/SettingsQuery';
import { FieldInputProps, FormikProps } from 'formik';
import PhoneNumberInput from '../../Utils/PhoneNumberInput';
import { GeneralSettingsRequest } from '../../../../client-sdk';
import { SocialLogin } from '../../../../types/settings';
// import { UseCreateGeneralSetting } from '../../../Query/CreateGeneralSettingQuery';
import LocationSearchInput from '../../../Stateless/LocationSearchInput';
import { splitPhoneNumber } from '../../../HelperFunctions/validationUtils';

interface FormValues {
  // Site Information
  siteName: string;
  siteNameArabic: string;
  contactEmail: string;
  contactPhone: string;
  location: string;
  siteDescription: string;
  siteDescriptionArabic: string;
  metaTitle: string;
  metaTitleArabic: string;
  metaKeywords: string;
  metaKeywordsArabic: string;
  metaDescription: string;
  metaDescriptionArabic: string;

  // Footer Information
  footerText: string;
  footerTextArabic: string;
  footerDescription: string;
  footerDescriptionArabic: string;
  preFooterTitle: string;
  preFooterTitleArabic: string;
  preFooterDescription: string;
  preFooterDescriptionArabic: string;

  // Footer Images
  preFooterImage: string | null;
  loginImage: string | null;
  featuredRestaurantImage: string | null;

  // App Store URLs
  itunesUrl: string;
  playStoreUrl: string;

  // Status Settings
  couponStatus: string;
  otpVerificationStatus: string;
  mailVerificationStatus: string;
  noContactDeliveryStatus: string;
  selfPickupStatus: string;
  preOrderStatus: string;
  showOutOfStockProducts: string;
  showStock: string;
  myRewardsToggle: string;
  myOffersToggle: string;
  referFriendToggle: string;
  preOrderToggle: string;

  // Twilio Settings
  twilioSid: string;
  twilioToken: string;
  twilioFrom: string;

  // Delivery Settings
  deliveryFeeStatus: string;
  // deliveryFeeType: string;
  commonDeliveryFee: string;
  feePerKilometer: string;
  deliveryBoyKilometerRange: string;

  // Service Settings
  serviceFeeType: string;
  serviceFee: string;

  // Peak Hours Settings
  peakHoursStatus: string;
  peakHoursData: {
    sunday: PeakHourEntry[];
    monday: PeakHourEntry[];
    tuesday: PeakHourEntry[];
    wednesday: PeakHourEntry[];
    thursday: PeakHourEntry[];
    friday: PeakHourEntry[];
    saturday: PeakHourEntry[];
  };

  // Order and Cart Settings
  orderHistoryExpire: string;
  orderHistoryDuration: string;
  abandonedCartMailStatus: string;
  sendMailAfter: string;

  // Referral and Security
  referFriends: string;
  offerPercentage: string;
  maxReferralAmount: string;
  passwordProtection: string;
  showCaptcha: string;
  invalidLoginSuspend: string;

  // Restaurant Settings
  featuredRestaurant: string;
  featuredPrice: string;
  commonCommission: string;
  commonDeliveryRadius: string;
  deliveryRadiusInKm: string;

  // Currency and Time Settings
  // apiKeyForCurrencyConverter: string;
  timePeriodForPreorder: string;
  timeZone: string;
  dateTimeFormat: string;
  emailStatus: string;
  smsStatus: string;
  deliveryRadiusType: string;

  // Loyalty Rewards
  loyaltyRewardsStatus: string;
  minimumTargetPoints: string;
  usdOneEquals: string;
  redeemPointValue: string;

  // Pre-order Settings
  preOrderMerchantAcceptanceTimeInSeconds: string;
  preOrderProcessStartBeforeMinutes: string;
  preOrderMinimumAdvanceMinutes: string;
  preOrderMaximumAdvanceDays: string;
  emergencyDriverBonusAmount: string;
  emergencyDriverBonusPercentage: string;
  driverUnavailableTimeoutExtension: string;
  enableAutomaticTimeoutExtension: string;

  // Social Media Login
  googleLoginStatus: string;
  facebookLoginStatus: string;
  appleLoginStatus: string;

  // Order Alert Settings
  merchantAccepted: string;
  orderPreparing: string;
  readyForPickup: string;
}

interface PeakHourEntry {
  startTime: string;
  endTime: string;
  amount: string;
}

interface GeneralSettingsProps {
  onClose?: () => void;
  generalSettingsData?: GeneralSettingsRequest[];
  socialLoginData?: SocialLogin | null;
}

// Validation schemas for different sections
const siteInfoValidationSchema = Yup.object().shape({
  siteName: Yup.string()
    .required('Please enter site Name')
    .max(50, 'Site Name cannot exceed 50 characters')
    .matches(/^[^0-9]+$/, 'Site Name cannot contain numbers')
    .test(
      'no-leading-trailing-spaces',
      'Site Name cannot start or end with spaces',
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      'Site Name cannot be only spaces',
      (value) => value?.trim().length > 0
    )
    .test(
      'not-start-with-special',
      'Site Name cannot start with a special character',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if first character is a special character
        return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(trimmed);
      }
    )
    .test(
      'not-only-special-chars',
      'Site Name cannot consist only of special characters',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if the string contains at least one letter or space (not just special chars)
        return /[a-zA-Z\s]/.test(trimmed);
      }
    ),
  siteNameArabic: Yup.string()
    .required('Please enter Site Name in Arabic')
    .max(50, 'Restaurant name cannot exceed 50 characters')
    .test(
      'no-leading-trailing-spaces',
      'Site Name cannot start or end with spaces',
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      'Site Name cannot be only spaces',
      (value) => value?.trim().length > 0
    )
    .matches(
      /^[\u0600-\u06FF\s!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/,
      'Site Name must contain only Arabic letters, spaces, and special characters'
    )
    .matches(/^[^0-9]+$/, 'Site Name cannot contain numbers')
    .test(
      'not-start-with-special',
      'Site Name cannot start with a special character',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if first character is a special character
        return !/^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(trimmed);
      }
    )
    .test(
      'not-only-special-chars',
      'Site Name cannot consist only of special characters',
      (value) => {
        if (!value) return true;
        const trimmed = value.trim();
        if (trimmed.length === 0) return true;
        // Check if the string contains at least one Arabic letter or space (not just special chars)
        return /[\u0600-\u06FF\s]/.test(trimmed);
      }
    ),
  contactEmail: Yup.string()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
      'Please enter a valid email address'
    )
    .required('Email is required'),
  contactPhone: Yup.string()
    .required('Contact phone is required')
    .matches(
      /^(?![\s\-\(\)0]+$)\+?[0-9\s\-\(\)]+$/,
      'Invalid phone number format'
    )
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    ),
  location: Yup.string()
    .max(250, 'Location cannot exceed 250 characters')
    .test(
      'not-only-spaces',
      'Location cannot be only spaces',
      (value) => !value || value?.trim().length > 0
    ),
  siteDescription: Yup.string()
    .test('has-content', 'Please enter Site Description', (value) => {
      if (!value) return false;
      // Remove HTML tags and whitespace
      const text = value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, '')
        .trim();
      // Check for at least one letter or number
      return /[a-zA-Z0-9]/.test(text);
    })
    .required('Please enter Site Description'),
  siteDescriptionArabic: Yup.string()
    .test(
      'has-arabic-content',
      'Please enter valid Site Description Arabic content',
      (value) => {
        if (!value) return false;
        const text = value
          .replace(/<[^>]*>/g, '') // Remove HTML tags
          .replace(/&nbsp;/g, '') // Remove &nbsp;
          .trim();

        // Check for at least one Arabic character and only Arabic-compatible content
        const arabicRegex = /^[\u0600-\u06FF\s0-9،؛؟.,\-!()]+$/; // Includes Arabic letters, digits, common punctuation
        return arabicRegex.test(text);
      }
    )
    .required('Please enter Site Description in Arabic'),
  metaTitle: Yup.string(),
  metaTitleArabic: Yup.string(),
  metaKeywords: Yup.string(),
  metaKeywordsArabic: Yup.string(),
  metaDescription: Yup.string(),
  metaDescriptionArabic: Yup.string(),
});

const footerValidationSchema = Yup.object().shape({
  footerText: Yup.string(),

  footerTextArabic: Yup.string(),

  footerDescription: Yup.string(),

  footerDescriptionArabic: Yup.string(),
  preFooterTitle: Yup.string(),
  preFooterTitleArabic: Yup.string(),
  preFooterDescription: Yup.string(),
  preFooterDescriptionArabic: Yup.string(),
  preFooterImage: Yup.string().nullable().url('Must be a valid URL'),
  loginImage: Yup.string().nullable().url('Must be a valid URL'),
  featuredRestaurantImage: Yup.string().nullable().url('Must be a valid URL'),
});

const appStoreValidationSchema = Yup.object().shape({
  itunesUrl: Yup.string()
    .required('iTunes URL is required')
    .url('Invalid URL format'),
  playStoreUrl: Yup.string()
    .required('Play Store URL is required')
    .url('Invalid URL format'),
});

const restaurantValidationSchema = Yup.object().shape({
  featuredRestaurant: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid featured restaurant status'
  ),
  featuredPrice: Yup.string().when('featuredRestaurant', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Featured price is required when featured restaurant is enabled'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
  commonCommission: Yup.string()
    .required('Common commission is required')
    .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
    .test('positive-number', 'Must be a positive number', (value) => {
      return value ? parseFloat(value) > 0 : true;
    }),
  commonDeliveryRadius: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid common delivery radius status'
  ),
  deliveryRadiusInKm: Yup.string().when('commonDeliveryRadius', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Delivery radius in km is required when common delivery radius is enabled'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
});

const deliveryValidationSchema = Yup.object().shape({
  deliveryFeeStatus: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid delivery fee status'
  ),
  // deliveryFeeType: Yup.string()
  //     .when('deliveryFeeStatus', {
  //         is: 'Enable',
  //         then: (schema) => schema
  //             .required('Delivery fee type is required when delivery fee is enabled')
  //             .oneOf(['Common Delivery Fee', 'Fee Per Kilometer'], 'Invalid delivery fee type'),
  //     }),
  commonDeliveryFee: Yup.string().when('deliveryFeeStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Common delivery fee is required when delivery fee is enabled'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
  feePerKilometer: Yup.string().when('deliveryFeeStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required('Per kilometer fee is required when delivery fee is enabled')
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
  deliveryBoyKilometerRange: Yup.string()
    .required('Delivery boy kilometer range is required')
    .matches(/^\d+$/, 'Must be a valid number')
    .test('positive-number', 'Must be a positive number', (value) => {
      return value ? parseInt(value) > 0 : true;
    }),
});
const serviceFeeValidationSchema = Yup.object().shape({
  serviceFeeType: Yup.string()
    .required('Service fee type is required')
    .oneOf(['Fixed Fee', 'Percentage Fee'], 'Invalid service fee type'),

  serviceFee: Yup.number()
    .when('serviceFeeType', {
      is: 'Percentage Fee',
      then: (schema) =>
        schema
          .required('Service fee percentage is required')
          .moreThan(0, 'Percentage must be greater than 0')
          .max(100, 'Percentage cannot be greater than 100'),
    })
    .when('serviceFeeType', {
      is: 'Fixed Fee',
      then: (schema) =>
        schema
          .required('Fixed service fee is required')
          .integer('Fixed fee must be a whole number')
          .moreThan(0, 'Fixed fee must be greater than 0'),
    }),
});

const orderCartValidationSchema = Yup.object().shape({
  orderHistoryExpire: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid order history expire status'
  ),
  orderHistoryDuration: Yup.string().when('orderHistoryExpire', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Order history duration is required when order history expire is enabled'
        )
        .matches(/^\d+$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseInt(value) >= 0 : true;
        }),
  }),
  abandonedCartMailStatus: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid abandoned cart mail status'
  ),
  sendMailAfter: Yup.string().when('abandonedCartMailStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Send mail after is required when abandoned cart mail status is enabled'
        )
        .matches(/^\d+$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseInt(value) > 0 : true;
        }),
  }),
});

const twilioValidationSchema = Yup.object().shape({
  twilioSid: Yup.string(),
  twilioToken: Yup.string(),
  twilioFrom: Yup.string(),
});

const peakHoursValidationSchema = Yup.object().shape({
  peakHoursStatus: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid peak hours status'
  ),
  peakHoursData: Yup.object().shape({
    sunday: Yup.array().of(
      Yup.object()
        .shape({
          startTime: Yup.string().required('Start time is required'),
          endTime: Yup.string().required('End time is required'),
          amount: Yup.string()
            .min(0, 'Amount must be zero or greater')
            .required('Amount is required'),
        })
        .test(
          'time-validation',
          'Start and end time cannot both be 00:00',
          function (value) {
            if (!value) return true;
            const startTimeDisplay = value.startTime.replace(':00Z', '');
            const endTimeDisplay = value.endTime.replace(':00Z', '');
            return !(
              startTimeDisplay === '00:00' && endTimeDisplay === '00:00'
            );
          }
        )
    ),
    monday: Yup.array().of(
      Yup.object()
        .shape({
          startTime: Yup.string().required('Start time is required'),
          endTime: Yup.string().required('End time is required'),
          amount: Yup.string()
            .min(0, 'Amount must be zero or greater')
            .required('Amount is required'),
        })
        .test(
          'time-validation',
          'Start and end time cannot both be 00:00',
          function (value) {
            if (!value) return true;
            const startTimeDisplay = value.startTime.replace(':00Z', '');
            const endTimeDisplay = value.endTime.replace(':00Z', '');
            return !(
              startTimeDisplay === '00:00' && endTimeDisplay === '00:00'
            );
          }
        )
    ),
    tuesday: Yup.array().of(
      Yup.object()
        .shape({
          startTime: Yup.string().required('Start time is required'),
          endTime: Yup.string().required('End time is required'),
          amount: Yup.string()
            .min(0, 'Amount must be zero or greater')
            .required('Amount is required'),
        })
        .test(
          'time-validation',
          'Start and end time cannot both be 00:00',
          function (value) {
            if (!value) return true;
            const startTimeDisplay = value.startTime.replace(':00Z', '');
            const endTimeDisplay = value.endTime.replace(':00Z', '');
            return !(
              startTimeDisplay === '00:00' && endTimeDisplay === '00:00'
            );
          }
        )
    ),
    wednesday: Yup.array().of(
      Yup.object()
        .shape({
          startTime: Yup.string().required('Start time is required'),
          endTime: Yup.string().required('End time is required'),
          amount: Yup.string()
            .min(0, 'Amount must be zero or greater')
            .required('Amount is required'),
        })
        .test(
          'time-validation',
          'Start and end time cannot both be 00:00',
          function (value) {
            if (!value) return true;
            const startTimeDisplay = value.startTime.replace(':00Z', '');
            const endTimeDisplay = value.endTime.replace(':00Z', '');
            return !(
              startTimeDisplay === '00:00' && endTimeDisplay === '00:00'
            );
          }
        )
    ),
    thursday: Yup.array().of(
      Yup.object()
        .shape({
          startTime: Yup.string().required('Start time is required'),
          endTime: Yup.string().required('End time is required'),
          amount: Yup.string()
            .min(0, 'Amount must be zero or greater')
            .required('Amount is required'),
        })
        .test(
          'time-validation',
          'Start and end time cannot both be 00:00',
          function (value) {
            if (!value) return true;
            const startTimeDisplay = value.startTime.replace(':00Z', '');
            const endTimeDisplay = value.endTime.replace(':00Z', '');
            return !(
              startTimeDisplay === '00:00' && endTimeDisplay === '00:00'
            );
          }
        )
    ),
    friday: Yup.array().of(
      Yup.object()
        .shape({
          startTime: Yup.string().required('Start time is required'),
          endTime: Yup.string().required('End time is required'),
          amount: Yup.string()
            .min(0, 'Amount must be zero or greater')
            .required('Amount is required'),
        })
        .test(
          'time-validation',
          'Start and end time cannot both be 00:00',
          function (value) {
            if (!value) return true;
            const startTimeDisplay = value.startTime.replace(':00Z', '');
            const endTimeDisplay = value.endTime.replace(':00Z', '');
            return !(
              startTimeDisplay === '00:00' && endTimeDisplay === '00:00'
            );
          }
        )
    ),
    saturday: Yup.array().of(
      Yup.object()
        .shape({
          startTime: Yup.string().required('Start time is required'),
          endTime: Yup.string().required('End time is required'),
          amount: Yup.string()
            .min(0, 'Amount must be zero or greater')
            .required('Amount is required'),
        })
        .test(
          'time-validation',
          'Start and end time cannot both be 00:00',
          function (value) {
            if (!value) return true;
            const startTimeDisplay = value.startTime.replace(':00Z', '');
            const endTimeDisplay = value.endTime.replace(':00Z', '');
            return !(
              startTimeDisplay === '00:00' && endTimeDisplay === '00:00'
            );
          }
        )
    ),
  }),
});

const loyaltyValidationSchema = Yup.object().shape({
  loyaltyRewardsStatus: Yup.string().oneOf(
    ['Active', 'Deactive'],
    'Invalid loyalty rewards status'
  ),
  minimumTargetPoints: Yup.string().when('loyaltyRewardsStatus', {
    is: 'Active',
    then: (schema) =>
      schema
        .required(
          'Minimum target points is required when loyalty rewards is active'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
  usdOneEquals: Yup.string().when('loyaltyRewardsStatus', {
    is: 'Active',
    then: (schema) =>
      schema
        .required(
          'currency To Points Ratio is required when loyalty rewards is active'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
  redeemPointValue: Yup.string().when('loyaltyRewardsStatus', {
    is: 'Active',
    then: (schema) =>
      schema
        .required(
          'Redeem point value is required when loyalty rewards is active'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
});

const referralSecurityValidationSchema = Yup.object().shape({
  referFriends: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid refer friends status'
  ),
  offerPercentage: Yup.string().when('referFriends', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required('Offer percentage is required when refer friends is enabled')
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
  maxReferralAmount: Yup.string().when('referFriends', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Max referral amount is required when refer friends is enabled'
        )
        .matches(/^\d*\.?\d*$/, 'Must be a valid number (with Decimal)')
        .test('positive-number', 'Must be a positive number', (value) => {
          return value ? parseFloat(value) > 0 : true;
        }),
  }),
  passwordProtection: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid password protection status'
  ),
  showCaptcha: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid show captcha status'
  ),
  invalidLoginSuspend: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid invalid login suspend status'
  ),
});

const socialMediaLoginValidationSchema = Yup.object().shape({
  googleLoginStatus: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid Google login status'
  ),
  facebookLoginStatus: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid Facebook login status'
  ),
  appleLoginStatus: Yup.string().oneOf(
    ['Enable', 'Disable'],
    'Invalid Apple login status'
  ),
});

const preOrderValidationSchema = Yup.object().shape({
  preOrderStatus: Yup.string()
    .oneOf(['Enable', 'Disable'], 'Invalid pre-order status')
    .required('Pre-order status is required'),
  preOrderMerchantAcceptanceTimeInSeconds: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Merchant acceptance time is required when pre-order is enabled'
        )
        .matches(/^\d+$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) =>
          value ? parseInt(value, 10) > 0 : true
        ),
  }),
  preOrderProcessStartBeforeMinutes: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Process start before (in minutes) is required when pre-order is enabled'
        )
        .matches(/^\d+$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) =>
          value ? parseInt(value, 10) > 0 : true
        ),
  }),
  preOrderMinimumAdvanceMinutes: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Minimum advance (in minutes) is required when pre-order is enabled'
        )
        .matches(/^\d+$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) =>
          value ? parseInt(value, 10) > 0 : true
        ),
  }),
  preOrderMaximumAdvanceDays: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Maximum advance (in days) is required when pre-order is enabled'
        )
        .matches(/^\d+$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) =>
          value ? parseInt(value, 10) > 0 : true
        ),
  }),
  emergencyDriverBonusAmount: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Emergency driver bonus amount is required when pre-order is enabled'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('non-negative-number', 'Must be 0 or greater', (value) =>
          value ? parseFloat(value) >= 0 : true
        ),
  }),
  emergencyDriverBonusPercentage: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Emergency driver bonus percentage is required when pre-order is enabled'
        )
        .matches(/^\d+(\.\d{1,2})?$/, 'Must be a valid number')
        .test('non-negative-number', 'Must be 0 or greater', (value) =>
          value ? parseFloat(value) >= 0 : true
        ),
  }),
  driverUnavailableTimeoutExtension: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema
        .required(
          'Driver unavailable timeout extension is required when pre-order is enabled'
        )
        .matches(/^\d+$/, 'Must be a valid number')
        .test('positive-number', 'Must be a positive number', (value) =>
          value ? parseInt(value, 10) > 0 : true
        ),
  }),
  enableAutomaticTimeoutExtension: Yup.string().when('preOrderStatus', {
    is: 'Enable',
    then: (schema) =>
      schema.required(
        'Automatic timeout extension selection is required when pre-order is enabled'
      ),
  }),
});

// Currency and Time Settings Validation
const currencyTimeValidationSchema = Yup.object().shape({
  // apiKeyForCurrencyConverter: Yup.string()
  //     .required('API key for currency converter is required')
  //     .test(
  //         'not-only-spaces',
  //         'API key for currency converter cannot be only spaces',
  //         (value) => value?.trim().length > 0
  //     ),
  /*timePeriodForPreorder: Yup.string()
        .required('Time period for Preparing for Delivery of Pre order is required')
        .matches(/^\d+$/, 'Time period must be a positive integer'),*/
  timeZone: Yup.string().required('Time zone is required'),
  dateTimeFormat: Yup.string().required('Date time format is required'),
  emailStatus: Yup.string()
    .oneOf(['Enable', 'Disable'], 'Invalid email status')
    .required('Email status is required'),
  smsStatus: Yup.string()
    .oneOf(['Enable', 'Disable'], 'Invalid SMS status')
    .required('SMS status is required'),
  deliveryRadiusType: Yup.string()
    .oneOf(['Kilometers', 'Miles'], 'Invalid delivery radius type')
    .required('Delivery radius type is required'),
});

const orderAlertValidationSchema = Yup.object().shape({
  merchantAccepted: Yup.string()
    .matches(/^\d+$/, 'Must be a valid number')
    .test('positive-number', 'Must be a positive number', (value) => {
      return value ? parseInt(value) > 0 : true;
    }),
  orderPreparing: Yup.string()
    .matches(/^\d+$/, 'Must be a valid number')
    .test('positive-number', 'Must be a positive number', (value) => {
      return value ? parseInt(value) > 0 : true;
    }),
  readyForPickup: Yup.string()
    .matches(/^\d+$/, 'Must be a valid number')
    .test('positive-number', 'Must be a positive number', (value) => {
      return value ? parseInt(value) > 0 : true;
    }),
});

// Main validation schema
const validationSchema = Yup.object().shape({
  ...siteInfoValidationSchema.fields,
  ...footerValidationSchema.fields,
  ...appStoreValidationSchema.fields,
  ...restaurantValidationSchema.fields,
  ...deliveryValidationSchema.fields,
  ...serviceFeeValidationSchema.fields,
  ...orderCartValidationSchema.fields,
  ...twilioValidationSchema.fields,
  ...peakHoursValidationSchema.fields,
  ...loyaltyValidationSchema.fields,
  ...referralSecurityValidationSchema.fields,
  ...preOrderValidationSchema.fields,
  ...currencyTimeValidationSchema.fields,
  ...socialMediaLoginValidationSchema.fields,
  ...orderAlertValidationSchema.fields,
  // Add other validation fields as needed
});

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  generalSettingsData,
  socialLoginData,
}) => {
  //   const { mutate: CreateGeneralSetting} =
  //     UseCreateGeneralSetting();
  const [selectedCountryCode, setSelectedCountryCode] = useState('+962');
  const { mutateAsync: createGeneralSetting, isPending: isSaving } =
    useSettingsCreate();

  const initialValues: FormValues = {
    // Site Information
    siteName: '',
    siteNameArabic: '',
    contactEmail: '',
    contactPhone: '',
    location: '',
    siteDescription: '',
    siteDescriptionArabic: '',
    metaTitle: '',
    metaTitleArabic: '',
    metaKeywords: '',
    metaKeywordsArabic: '',
    metaDescription: '',
    metaDescriptionArabic: '',

    // Footer Information
    footerText: '',
    footerTextArabic: '',
    footerDescription: '',
    footerDescriptionArabic: '',
    preFooterTitle: '',
    preFooterTitleArabic: '',
    preFooterDescription: '',
    preFooterDescriptionArabic: '',

    // Footer Images
    preFooterImage: null,
    loginImage: null,
    featuredRestaurantImage: null,

    // App Store URLs
    itunesUrl: '',
    playStoreUrl: '',

    // Status Settings
    couponStatus: 'Enable',
    otpVerificationStatus: 'Enable',
    mailVerificationStatus: 'Enable',
    noContactDeliveryStatus: 'Enable',
    selfPickupStatus: 'Enable',
    preOrderStatus: 'Enable',
    showOutOfStockProducts: 'No',
    showStock: 'Yes',
    myRewardsToggle: 'Enable',
    myOffersToggle: 'Enable',
    referFriendToggle: 'Enable',
    preOrderToggle: 'Enable',

    // Twilio Settings
    twilioSid: '',
    twilioToken: '',
    twilioFrom: '',

    // Delivery Settings
    deliveryFeeStatus: 'Enable',
    // deliveryFeeType: '',
    commonDeliveryFee: '',
    feePerKilometer: '',
    deliveryBoyKilometerRange: '',

    // Service Settings
    serviceFeeType: 'Fixed Fee',
    serviceFee: '',
    // Peak Hours Settings
    peakHoursStatus: 'Disable',
    peakHoursData: {
      sunday: [],
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
    },

    // Order and Cart Settings
    orderHistoryExpire: 'Disable',
    orderHistoryDuration: '',
    abandonedCartMailStatus: 'Enable',
    sendMailAfter: '',

    // Referral and Security
    referFriends: 'Enable',
    offerPercentage: '',
    maxReferralAmount: '',
    passwordProtection: 'Disable',
    showCaptcha: 'Disable',
    invalidLoginSuspend: 'Disable',

    // Restaurant Settings
    featuredRestaurant: 'Enable',
    featuredPrice: '',
    commonCommission: '',
    commonDeliveryRadius: 'Disable',
    deliveryRadiusInKm: '',

    // Currency and Time Settings
    // apiKeyForCurrencyConverter: '',
    timePeriodForPreorder: '',
    timeZone: '',
    dateTimeFormat: '',
    emailStatus: 'Enable',
    smsStatus: 'Enable',
    deliveryRadiusType: '',

    // Loyalty Rewards
    loyaltyRewardsStatus: 'Active',
    minimumTargetPoints: '',
    usdOneEquals: '',
    redeemPointValue: '',
    // Pre-order Settings
    preOrderMerchantAcceptanceTimeInSeconds: '',
    preOrderProcessStartBeforeMinutes: '',
    preOrderMinimumAdvanceMinutes: '',
    preOrderMaximumAdvanceDays: '',
    emergencyDriverBonusAmount: '',
    emergencyDriverBonusPercentage: '',
    driverUnavailableTimeoutExtension: '',
    enableAutomaticTimeoutExtension: '',

    // Social Media Login
    googleLoginStatus: 'Disable',
    facebookLoginStatus: 'Disable',
    appleLoginStatus: 'Disable',

    // Order Alert Settings
    merchantAccepted: '1',
    orderPreparing: '3',
    readyForPickup: '3',
  };

  // Populate form with existing data if available
  const [formValues, setFormValues] = useState<FormValues>(initialValues);

  useEffect(() => {
    if (generalSettingsData && generalSettingsData.length > 0) {
      const existingData = generalSettingsData[0];
      console.log('existingData', existingData); // Assuming we take the first item
      console.log('Populating form with data:', {
        siteName: existingData.siteName,
        contactEmail: existingData.contactEmail,
        contactPhone: existingData.contactPhone,
      });

      // Extract country code from phone number
      if (existingData.contactPhone) {
        const phoneNumber = existingData.contactPhone.replace(/\D/g, ''); // Remove all non-digits
        // const countryCodeDigits = phoneNumber.slice(0, -10); // Get all digits except last 10

        const { countryCode: countryCodeDigits } =
          splitPhoneNumber(phoneNumber);
        if (countryCodeDigits) {
          setSelectedCountryCode(`+${countryCodeDigits}`);
        }
      }
      const populatedValues: FormValues = {
        ...initialValues,
        // Site Information
        siteName: existingData.siteName || '',
        siteNameArabic: existingData.siteNameArabic || '',
        contactEmail: existingData.contactEmail || '',
        contactPhone: (() => {
          if (!existingData.contactPhone) return '';
          // Extract last 10 digits for phone number
          const phoneNumber = existingData.contactPhone.replace(/\D/g, ''); // Remove all non-digits
          return phoneNumber.slice(-10); // Get last 10 digits
        })(),
        location: (existingData as any).location || '',
        siteDescription: existingData.siteDescription || '',
        siteDescriptionArabic: existingData.siteDescriptionArabic || '',
        metaTitle: existingData.metaTitle || '',
        metaTitleArabic: existingData.metaTitleArabic || '',
        metaKeywords: existingData.metaKeywords || '',
        metaKeywordsArabic: existingData.metaKeywordsArabic || '',
        metaDescription: existingData.metaDescription || '',
        metaDescriptionArabic: existingData.metaDescriptionArabic || '',

        // Footer Information
        footerText: existingData.footerText || '',
        footerTextArabic: existingData.footerTextArabic || '',
        footerDescription: existingData.footerDescription || '',
        footerDescriptionArabic: existingData.footerDescriptionArabic || '',
        preFooterTitle: existingData.preFooterTitle || '',
        preFooterTitleArabic: existingData.preFooterTitleArabic || '',
        preFooterDescription: existingData.preFooterDescription || '',
        preFooterDescriptionArabic:
          existingData.preFooterDescriptionArabic || '',

        // Footer Images
        preFooterImage: existingData.preFooterImage || null,
        loginImage: existingData.loginImage || null,
        featuredRestaurantImage: existingData.featuredRestaurantImage || null,

        // App Store URLs
        itunesUrl: existingData.itunesUrl || '',
        playStoreUrl: existingData.playStoreUrl || '',

        // Status Settings - Convert boolean to string
        couponStatus: existingData.couponStatus ? 'Enable' : 'Disable',
        otpVerificationStatus: existingData.otpVerificationStatus?.status
          ? 'Enable'
          : 'Disable',
        mailVerificationStatus: existingData.mailVerificationStatus
          ? 'Enable'
          : 'Disable',
        noContactDeliveryStatus: existingData.noContactDeliveryStatus
          ? 'Enable'
          : 'Disable',
        selfPickupStatus: existingData.selfPickupStatus ? 'Enable' : 'Disable',
        preOrderStatus: existingData.preOrderStatus?.status
          ? 'Enable'
          : 'Disable',
        showOutOfStockProducts: existingData.showOutOfStockProducts
          ? 'Yes'
          : 'No',
        showStock: existingData.showStock ? 'Yes' : 'No',
        myRewardsToggle: (existingData as any).myRewardsToggle
          ? 'Enable'
          : 'Disable',
        myOffersToggle: (existingData as any).myOffersToggle
          ? 'Enable'
          : 'Disable',
        referFriendToggle: (existingData as any).referFriendToggle
          ? 'Enable'
          : 'Disable',
        preOrderToggle: (existingData as any).preOrderToggle
          ? 'Enable'
          : 'Disable',

        // Twilio Settings - Extract from otpVerificationStatus
        twilioSid: existingData.otpVerificationStatus?.twilioSid || '',
        twilioToken: existingData.otpVerificationStatus?.twilioToken || '',
        twilioFrom: existingData.otpVerificationStatus?.twilioFrom || '',

        // Delivery Settings
        deliveryFeeStatus: existingData.deliveryFeeStatus
          ? 'Enable'
          : 'Disable',
        // deliveryFeeType: existingData.deliveryFeeType === 'common' ? 'Common Delivery Fee' :
        //     existingData.deliveryFeeType === 'perKilometer' ? 'Fee Per Kilometer' : '',
        commonDeliveryFee: existingData.commonDeliveryFee?.toString() || '',
        feePerKilometer: existingData.feePerKM?.toString() || '',
        deliveryBoyKilometerRange:
          existingData.deliveryBoyKilometerRange?.toString() || '',
        // Service Fee Settings
        serviceFeeType:
          existingData.serviceFeeType === 'fixedFee'
            ? 'Fixed Fee'
            : existingData.serviceFeeType === 'percentageFee'
              ? 'Percentage Fee'
              : '',
        serviceFee: existingData.serviceFee?.toString() || '',

        // Peak Hours Settings
        peakHoursStatus: existingData.peakHoursDeliveryFeeStatus
          ? 'Enable'
          : 'Disable',
        peakHoursData: {
          sunday: Array.isArray(
            existingData.peakHoursDeliveryFeeStatus?.peakHoursConfiguration
              ?.sunday?.timeRanges
          )
            ? existingData.peakHoursDeliveryFeeStatus.peakHoursConfiguration.sunday?.timeRanges.map(
                (item: any) =>
                  ({
                    startTime: item.startTime,
                    endTime: item.endTime,
                    amount: item.additionalFee?.toString() || '',
                  }) as PeakHourEntry
              )
            : [],
          monday: Array.isArray(
            existingData.peakHoursDeliveryFeeStatus?.peakHoursConfiguration
              ?.monday?.timeRanges
          )
            ? existingData.peakHoursDeliveryFeeStatus.peakHoursConfiguration.monday?.timeRanges.map(
                (item: any) =>
                  ({
                    startTime: item.startTime,
                    endTime: item.endTime,
                    amount: item.additionalFee?.toString() || '',
                  }) as PeakHourEntry
              )
            : [],
          tuesday: Array.isArray(
            existingData.peakHoursDeliveryFeeStatus?.peakHoursConfiguration
              ?.tuesday?.timeRanges
          )
            ? existingData.peakHoursDeliveryFeeStatus.peakHoursConfiguration.tuesday?.timeRanges.map(
                (item: any) =>
                  ({
                    startTime: item.startTime,
                    endTime: item.endTime,
                    amount: item.additionalFee?.toString() || '',
                  }) as PeakHourEntry
              )
            : [],
          wednesday: Array.isArray(
            existingData.peakHoursDeliveryFeeStatus?.peakHoursConfiguration
              ?.wednesday?.timeRanges
          )
            ? existingData.peakHoursDeliveryFeeStatus.peakHoursConfiguration.wednesday?.timeRanges.map(
                (item: any) =>
                  ({
                    startTime: item.startTime,
                    endTime: item.endTime,
                    amount: item.additionalFee?.toString() || '',
                  }) as PeakHourEntry
              )
            : [],
          thursday: Array.isArray(
            existingData.peakHoursDeliveryFeeStatus?.peakHoursConfiguration
              ?.thursday?.timeRanges
          )
            ? existingData.peakHoursDeliveryFeeStatus.peakHoursConfiguration.thursday?.timeRanges.map(
                (item: any) =>
                  ({
                    startTime: item.startTime,
                    endTime: item.endTime,
                    amount: item.additionalFee?.toString() || '',
                  }) as PeakHourEntry
              )
            : [],
          friday: Array.isArray(
            existingData.peakHoursDeliveryFeeStatus?.peakHoursConfiguration
              ?.friday?.timeRanges
          )
            ? existingData.peakHoursDeliveryFeeStatus.peakHoursConfiguration.friday?.timeRanges.map(
                (item: any) =>
                  ({
                    startTime: item.startTime,
                    endTime: item.endTime,
                    amount: item.additionalFee?.toString() || '',
                  }) as PeakHourEntry
              )
            : [],
          saturday: Array.isArray(
            existingData.peakHoursDeliveryFeeStatus?.peakHoursConfiguration
              ?.saturday?.timeRanges
          )
            ? existingData.peakHoursDeliveryFeeStatus.peakHoursConfiguration.saturday?.timeRanges.map(
                (item: any) =>
                  ({
                    startTime: item.startTime,
                    endTime: item.endTime,
                    amount: item.additionalFee?.toString() || '',
                  }) as PeakHourEntry
              )
            : [],
        },

        // Order and Cart Settings
        orderHistoryExpire: existingData.orderHistoryExpire?.status
          ? 'Enable'
          : 'Disable',
        orderHistoryDuration:
          existingData.orderHistoryExpire?.orderHistoryExpireDuration?.toString() ||
          '',
        abandonedCartMailStatus: existingData.abandonedCartMailStatus?.status
          ? 'Enable'
          : 'Disable',
        sendMailAfter:
          existingData.abandonedCartMailStatus?.sendAbandonedCartMailAfterHours?.toString() ||
          '',

        // Referral and Security
        referFriends: existingData.referFriends?.status ? 'Enable' : 'Disable',
        offerPercentage:
          existingData.referFriends?.offerPercentage?.toString() || '',
        maxReferralAmount:
          (
            (existingData as any).referFriends?.maxReferralAmount ??
            (existingData as any).referFriends?.maxReferralAmount
          )?.toString() || '',
        passwordProtection: existingData.passwordProtection
          ? 'Enable'
          : 'Disable',
        showCaptcha: existingData.showCaptcha ? 'Enable' : 'Disable',
        invalidLoginSuspend: existingData.invalidLoginSuspendCustomer
          ? 'Enable'
          : 'Disable',

        // Restaurant Settings
        featuredRestaurant: existingData.featuredRestaurant?.status
          ? 'Enable'
          : 'Disable',
        featuredPrice:
          existingData.featuredRestaurant?.featuredPricePerDay?.toString() ||
          '',
        commonCommission:
          existingData.commonCommissionPercentage?.toString() || '',
        commonDeliveryRadius: existingData.commonDeliveryRadius?.status
          ? 'Enable'
          : 'Disable',
        deliveryRadiusInKm:
          existingData.commonDeliveryRadius?.deliveryRadius?.toString() || '',

        // Currency and Time Settings
        // apiKeyForCurrencyConverter: existingData.currencyConverterApiKey || '',
        timePeriodForPreorder:
          (existingData as any).timeToPreparePreorderMinutes?.toString() || '',
        timeZone: existingData.timeZone || '',
        dateTimeFormat: existingData.dateTimeFormat || '',
        emailStatus: existingData.emailStatus ? 'Enable' : 'Disable',
        smsStatus: existingData.smsStatus ? 'Enable' : 'Disable',
        deliveryRadiusType:
          existingData.commonDeliveryRadius?.deliveryRadiusType === 'kilometers'
            ? 'Kilometers'
            : existingData.commonDeliveryRadius?.deliveryRadiusType === 'miles'
              ? 'Miles'
              : '',

        // Loyalty Rewards
        loyaltyRewardsStatus: existingData.loyaltyRewardsStatus?.status
          ? 'Active'
          : 'Deactive',
        minimumTargetPoints:
          existingData.loyaltyRewardsStatus?.minimumTargetPoints?.toString() ||
          '',
        usdOneEquals:
          existingData.loyaltyRewardsStatus?.currencyToPointsRatio?.toString() ||
          '',
        redeemPointValue:
          existingData.loyaltyRewardsStatus?.redeemPointValue?.toString() || '',
        // Pre-order Settings
        preOrderMerchantAcceptanceTimeInSeconds:
          existingData.preOrderStatus?.merchantAcceptanceTimeInSeconds?.toString() ||
          '',
        preOrderProcessStartBeforeMinutes:
          existingData.preOrderStatus?.processStartBeforeMinutes?.toString() ||
          '',
        preOrderMinimumAdvanceMinutes:
          existingData.preOrderStatus?.minimumAdvanceMinutes?.toString() || '',
        preOrderMaximumAdvanceDays:
          existingData.preOrderStatus?.maximumAdvanceDays?.toString() || '',
        emergencyDriverBonusAmount:
          (existingData as any).emergencyDriverBonusAmount?.toString() || '',
        emergencyDriverBonusPercentage:
          (existingData as any).emergencyDriverBonusPercentage?.toString() ||
          '',
        driverUnavailableTimeoutExtension:
          (existingData as any).driverUnavailableTimeoutExtension?.toString() ||
          '',
        enableAutomaticTimeoutExtension: (existingData as any)
          .enableAutomaticTimeoutExtension
          ? 'Enable'
          : 'Disable',

        // Social Media Login
        googleLoginStatus: socialLoginData?.google?.isEnabled
          ? 'Enable'
          : 'Disable',
        facebookLoginStatus: socialLoginData?.facebook?.isEnabled
          ? 'Enable'
          : 'Disable',
        appleLoginStatus: socialLoginData?.apple?.isEnabled
          ? 'Enable'
          : 'Disable',

        // Order Alert Settings
        merchantAccepted: (existingData as any).orderAlertSettings?.merchantAccepted || '1',
        orderPreparing: (existingData as any).orderAlertSettings?.orderPreparing || '1',
        readyForPickup: (existingData as any).orderAlertSettings?.readyForPickup || '1',
      };
      setFormValues(populatedValues);
    } else if (socialLoginData && Object.keys(socialLoginData).length > 0) {
      // If only socialLoginData exists, update just those fields
      setFormValues((prev) => ({
        ...prev,
        googleLoginStatus: socialLoginData.google?.isEnabled
          ? 'Enable'
          : 'Disable',
        facebookLoginStatus: socialLoginData.facebook?.isEnabled
          ? 'Enable'
          : 'Disable',
        appleLoginStatus: socialLoginData.apple?.isEnabled
          ? 'Enable'
          : 'Disable',
      }));
    }
  }, [generalSettingsData, socialLoginData]);

  // Common submit handler for all settings
  const handleGeneralSettingsSubmit = async (values: FormValues) => {
    try {
      const sanitizeImageUrl = (input: unknown): string | undefined => {
        if (typeof input !== 'string') return undefined;
        const trimmed = input.trim();
        if (!trimmed || trimmed.toLowerCase() === 'string') return undefined;
        try {
          const url = new URL(trimmed);
          return url.protocol === 'http:' || url.protocol === 'https:'
            ? trimmed
            : undefined;
        } catch {
          return undefined;
        }
      };

      // Format contactPhone with country code before transformation
      const formattedValues = {
        ...values,
        contactPhone: `${selectedCountryCode}${values.contactPhone}`,
        preFooterImage: sanitizeImageUrl(values.preFooterImage),
        loginImage: sanitizeImageUrl(values.loginImage),
        featuredRestaurantImage: sanitizeImageUrl(
          values.featuredRestaurantImage
        ),
      };

      // Transform form data to the required API schema format
      const generalSettingsData: GeneralSettingsSchema =
        transformFormDataToSchema(formattedValues);

      // Create socialLoginData structure (preserve existing appId, buttonText, buttonTextArabic if they exist)
      const existingSocialData: SocialLogin | null = socialLoginData || null;
      const updatedSocialLoginData: SocialLogin = {
        google: {
          isEnabled: values.googleLoginStatus === 'Enable',
          appId: existingSocialData?.google?.appId ?? '',
          buttonText: existingSocialData?.google?.buttonText ?? '',
          buttonTextArabic: existingSocialData?.google?.buttonTextArabic ?? '',
        },
        facebook: {
          isEnabled: values.facebookLoginStatus === 'Enable',
          appId: existingSocialData?.facebook?.appId ?? '',
          buttonText: existingSocialData?.facebook?.buttonText ?? '',
          buttonTextArabic:
            existingSocialData?.facebook?.buttonTextArabic ?? '',
        },
        apple: {
          isEnabled: values.appleLoginStatus === 'Enable',
          appId: existingSocialData?.apple?.appId ?? '',
          buttonText: existingSocialData?.apple?.buttonText ?? '',
          buttonTextArabic: existingSocialData?.apple?.buttonTextArabic ?? '',
        },
      };

      console.log('Form Values (Order Alerts):', {
        merchantAccepted: values.merchantAccepted,
        orderPreparing: values.orderPreparing,
        readyForPickup: values.readyForPickup,
      });
      console.log('General Settings Data (API Format):', generalSettingsData);
      console.log('Order Alert Settings in API:', {
        orderAlertSettings: generalSettingsData.orderAlertSettings,
      });
      console.log('Social Login Data (API Format):', updatedSocialLoginData);

      try {
        await createGeneralSetting(
          {
            generalSettingsData: generalSettingsData as any,
            socialLoginData: updatedSocialLoginData as any,
          },
          {
            onSuccess: (res: any) => console.log('Success:', res),
            onError: (err: any) => console.error('Error:', err),
          }
        );
      } catch (error) {
        console.error('Submission Error:', error);
      }

      toast.success('General settings saved successfully');
    } catch (error: any) {
      console.error('Error saving general settings:', error);
      toast.error(error.message || 'Failed to save general settings');
    }
  };

  const allFields = [
    // Site Information Section
    {
      name: 'siteInfoSection',
      type: 'section',
      sectionTitle: 'Site Information',
      sectionLine: true,
    },
    {
      name: 'siteName',
      label: 'Site Name',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter site name',
    },
    {
      name: 'siteNameArabic',
      label: 'Site Name (In Arabic)',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter site name in Arabic',
    },
    {
      name: 'contactEmail',
      label: 'Contact E-mail',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter contact email',
    },
    // {
    //     name: 'contactPhone',
    //     label: 'Contact Phone',
    //     type: 'text',
    //     isRequired: true,
    //     placeholder: 'Enter contact phone',
    // },
    {
      name: 'contactPhone',
      label: 'Contact Phone',
      type: 'custom',
      isRequired: true,
      component: ({
        field,
        form,
      }: {
        field: FieldInputProps<any>;
        form: FormikProps<any>;
      }) => (
        <PhoneNumberInput
          field={field}
          form={form}
          selectedCountryCode={selectedCountryCode}
          onCountryCodeChange={setSelectedCountryCode}
        />
      ),
    },
    {
      name: 'location',
      label: 'Location',
      type: 'customWithError',
      isRequired: false,
      component: ({
        field,
        form,
      }: {
        field: FieldInputProps<any>;
        form: FormikProps<any>;
      }) => (
        <LocationSearchInput
          address={form.values[field.name]}
          setAddress={(val) => {
            form.setFieldValue(field.name, val);
            form.setFieldTouched(field.name, true, false);
          }}
          handlesearchLocation={({ formattedAddress }) => {
            form.setFieldValue(field.name, formattedAddress || '');
            form.setFieldTouched(field.name, true, false);
          }}
        />
      ),
    },
    {
      name: 'siteDescription',
      label: 'Site Description',
      type: 'richtext',
      isRequired: true,
      placeholder: 'Enter site description',
    },
    {
      name: 'siteDescriptionArabic',
      label: 'Site Description (In Arabic)',
      type: 'richtext',
      isRequired: true,
      placeholder: 'Enter site description in Arabic',
    },
    {
      name: 'metaTitle',
      label: 'Meta Title',
      type: 'text',
      placeholder: 'Enter meta title',
    },
    {
      name: 'metaTitleArabic',
      label: 'Meta Title (In Arabic)',
      type: 'text',
      placeholder: 'Enter meta title in Arabic',
    },
    {
      name: 'metaKeywords',
      label: 'Meta Keywords',
      type: 'text',
      placeholder: 'Enter meta keywords',
    },
    {
      name: 'metaKeywordsArabic',
      label: 'Meta Keywords (In Arabic)',
      type: 'text',
      placeholder: 'Enter meta keywords in Arabic',
    },
    {
      name: 'metaDescription',
      label: 'Meta Description',
      type: 'richtext',
      placeholder: 'Enter meta description',
    },
    {
      name: 'metaDescriptionArabic',
      label: 'Meta Description (In Arabic)',
      type: 'richtext',
      placeholder: 'Enter meta description in Arabic',
    },

    // Footer Information Section
    /*{
            name: 'footerSection',
            type: 'section',
            sectionTitle: 'Footer Information',
            sectionLine: true,
        },
        {
            name: 'footerText',
            label: 'Footer Text',
            type: 'text',
            placeholder: 'Enter footer text',
        },
        {
            name: 'footerTextArabic',
            label: 'Footer Text (In Arabic)',
            type: 'text',
            placeholder: 'Enter footer text in Arabic',
        },
        {
            name: 'footerDescription',
            label: 'Footer Description',
            type: 'richtext',
            placeholder: 'Enter footer description',
        },
        {
            name: 'footerDescriptionArabic',
            label: 'Footer Description (In Arabic)',
            type: 'richtext',
            placeholder: 'Enter footer description in Arabic',
        },*/

    // App Store URLs Section
    {
      name: 'appStoreSection',
      type: 'section',
      sectionTitle: 'App Store URLs',
      sectionLine: true,
    },
    {
      name: 'itunesUrl',
      label: 'iTunes URL',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter iTunes URL',
    },
    {
      name: 'playStoreUrl',
      label: 'Play Store URL',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter Play Store URL',
    },

    // Social URLs under App Store URLs
    {
      name: 'socialmediapagelinksSection',
      type: 'section',
      sectionTitle: 'Social Media Page Links',
      sectionLine: true,
    },
    {
      name: 'facebookUrl',
      label: 'Facebook URL',
      type: 'text',
      placeholder: 'Enter Facebook page URL',
    },
    {
      name: 'twitterUrl',
      label: 'Twitter URL',
      type: 'text',
      placeholder: 'Enter Twitter profile URL',
    },
    {
      name: 'instagramUrl',
      label: 'Instagram URL',
      type: 'text',
      placeholder: 'Enter Instagram profile URL',
    },
    {
      name: 'youtubeUrl',
      label: 'YouTube URL',
      type: 'text',
      placeholder: 'Enter YouTube channel URL',
    },
    {
      name: 'socialMediaLoginSection',
      type: 'section',
      sectionTitle: 'Social Media Login',
      sectionLine: true,
    },
    {
      name: 'googleLoginStatus',
      label: 'Google Account',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'facebookLoginStatus',
      label: 'Facebook Account',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'appleLoginStatus',
      label: 'Apple Account',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'orderAlertSection',
      type: 'section',
      sectionTitle: 'Order Alert Setting',
      sectionLine: true,
    },
    {
      name: 'merchantAccepted',
      label: 'Merchant Accepted (in minutes)',
      type: 'number',
      isRequired: false,
      placeholder: 'Enter time in minutes for merchant accepted alert',
    },
    {
      name: 'orderPreparing',
      label: 'Order Preparing (in minutes)',
      type: 'number',
      isRequired: false,
      placeholder: 'Enter time in minutes for order preparing alert',
    },
    {
      name: 'readyForPickup',
      label: 'Ready for Pickup (in minutes)',
      type: 'number',
      isRequired: false,
      placeholder: 'Enter time in minutes for ready for pickup alert',
    },
    {
      name: 'preOrderSection',
      type: 'section',
      sectionTitle: 'Pre-order Settings',
      sectionLine: true,
    },
    {
      name: 'preOrderStatus',
      label: 'Pre-order Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'preOrderMerchantAcceptanceTimeInSeconds',
      label: 'Merchant Acceptance Time (in Seconds)',
      type: 'number',
      isRequired: true,
      placeholder: 'Enter Merchant Acceptance Time in Seconds',
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },
    {
      name: 'preOrderProcessStartBeforeMinutes',
      label: 'Process Start Before (in Minutes)',
      type: 'number',
      isRequired: true,
      placeholder: 'Enter Process Start Before in Minutes',
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },
    {
      name: 'preOrderMinimumAdvanceMinutes',
      label: 'Minimum Advance (in Minutes)',
      type: 'number',
      isRequired: true,
      placeholder: 'Enter Minimum Advance in Minutes',
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },
    {
      name: 'preOrderMaximumAdvanceDays',
      label: 'Maximum Advance (in Days) (Default: 7)',
      type: 'number',
      isRequired: true,
      placeholder: 'Enter Maximum Advance in Days',
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },
    {
      name: 'emergencyDriverBonusAmount',
      label: 'Emergency Driver Bonus Amount',
      type: 'number',
      isRequired: true,
      placeholder: 'Enter Emergency Driver Bonus Amount',
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },
    {
      name: 'emergencyDriverBonusPercentage',
      label: 'Emergency Driver Bonus Percentage',
      type: 'number',
      isRequired: true,
      placeholder: 'Enter Emergency Driver Bonus Percentage',
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },
    {
      name: 'driverUnavailableTimeoutExtension',
      label: 'Driver Unavailable Timeout Extension (in Seconds)',
      type: 'number',
      isRequired: true,
      placeholder: 'Enter Driver Unavailable Timeout Extension in Seconds',
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },
    {
      name: 'enableAutomaticTimeoutExtension',
      label: 'Automatic Timeout Extension',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
      dependsOn: { field: 'preOrderStatus', value: 'Enable' },
    },

    // Status Settings Section
    {
      name: 'statusSection',
      type: 'section',
      sectionTitle: 'Status Settings',
      sectionLine: true,
    },
    {
      name: 'couponStatus',
      label: 'Coupon Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'selfPickupStatus',
      label: 'Self Pickup Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'smsStatus',
      label: 'SMS Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'otpVerificationStatus',
      label: 'OTP Verification Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'emailStatus',
      label: 'EMail Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'mailVerificationStatus',
      label: 'Mail Verification Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },

    // {
    //     name: 'preOrderStatus',
    //     label: 'Pre-order Status',
    //     type: 'radio',
    //     isRequired: true,
    //     radioOptions: ['Enable', 'Disable'],
    // },
    {
      name: 'deliveryBoySection',
      type: 'section',
      sectionTitle: 'Delivery Settings',
      sectionLine: true,
    },
    {
      name: 'deliveryRadiusType',
      label: 'Delivery Radius Type',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Kilometers', 'Miles'],
    },
    {
      name: 'commonDeliveryRadius',
      label: 'Common Delivery Radius',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'deliveryRadiusInKm',
      label: 'Delivery Radius (In km)',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter delivery radius in kilometers',
      dependsOn: { field: 'commonDeliveryRadius', value: 'Enable' },
    },
    {
      name: 'deliveryBoyKilometerRange',
      label: 'Delivery boy kilometer range (Manual Allocation)',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter delivery boy kilometer range',
      component: ({ field, form }: any) => (
        <Box>
          <TextField
            className="input-large"
            name={field.name}
            value={form.values[field.name]}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              form.setFieldValue(field.name, e.target.value);
              form.setFieldTouched(field.name, true);
            }}
            onBlur={() => form.setFieldTouched(field.name, true)}
            error={Boolean(form.touched[field.name] && form.errors[field.name])}
            placeholder="Enter delivery boy kilometer range"
            sx={{
              mb: 0,
              '& .MuiInputBase-root': {
                fontSize: 14,
                color: 'text.primary',
                width: '400px',
                height: '40px',
              },
              '& .MuiFormLabel-root': {
                fontSize: 14,
              },
            }}
          />
          {form.values.deliveryFeeStatus === 'Disable' && (
            <Typography
              sx={{
                color: '#d32f2f',
                fontSize: 12,
                mt: 1,
                fontStyle: 'italic',
              }}
            >
              ⚠️ Delivery person charges can still be set even when delivery
              fees are disabled. This may result in admin losses.
            </Typography>
          )}
        </Box>
      ),
    },
    // Delivery Settings Section
    {
      name: 'deliverySection',
      type: 'section',
      sectionTitle: 'Delivery Fee (Collecting delivery fee from the Customer)',
      sectionLine: true,
    },
    {
      name: 'deliveryFeeStatus',
      label: 'Delivery Fee Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
      component: ({ field, form }: any) => (
        <Box>
          <FormControl>
            <RadioGroup
              row
              name={field.name}
              value={form.values[field.name]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                form.setFieldValue(field.name, e.target.value);
                form.setFieldTouched(field.name, true);
              }}
              sx={{ gap: 2.5, mt: 0.5 }}
            >
              {['Enable', 'Disable'].map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      size="small"
                      sx={{
                        color: 'text.secondary',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.secondary',
                      }}
                    >
                      {option}
                    </Typography>
                  }
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: 14,
                      color: 'text.secondary',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
          {form.values[field.name] === 'Disable' && (
            <Typography
              sx={{
                color: '#d32f2f',
                fontSize: 12,
                mt: 1,
                fontStyle: 'italic',
              }}
            >
              ⚠️ If status is disabled, you can't collect delivery fee from
              customer. But you can set delivery person charge. It may lose to
              Admin.
            </Typography>
          )}
        </Box>
      ),
    },
    // {
    //     name: 'deliveryFeeType',
    //     label: 'Delivery Fee Type',
    //     type: 'radio',
    //     isRequired: true,
    //     radioOptions: ['Common Delivery Fee', 'Fee Per Kilometer'],
    //     dependsOn: { field: 'deliveryFeeStatus', value: 'Enable' },
    // },
    {
      name: 'commonDeliveryFee',
      isRequired: true,
      label: 'Common Delivery Fee',
      type: 'text',
      placeholder: 'Enter common delivery fee',
      dependsOn: { field: 'deliveryFeeStatus', value: 'Enable' },
    },
    {
      name: 'feePerKilometer',
      isRequired: true,
      label: 'Fee Per Kilometer',
      type: 'text',
      placeholder: 'Enter fee per kilometer',
      dependsOn: { field: 'deliveryFeeStatus', value: 'Enable' },
    },
    // Service Fee Section
    {
      name: 'serviceSection',
      type: 'section',
      sectionTitle: 'Service Fee (Collecting service fee from the Customer)',
      sectionLine: true,
    },
    {
      name: 'serviceFeeType',
      label: 'Service Fee Type',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Fixed Fee', 'Percentage Fee'],
    },
    {
      name: 'serviceFee',
      isRequired: true,
      label: (values: any) => {
        const serviceFeeType = values?.serviceFeeType;
        if (serviceFeeType === 'Fixed Fee') {
          return 'Fixed Fee';
        }
        return 'Percentage Fee';
      },
      type: 'text',
      placeholder: (values: any) => {
        const serviceFeeType = values?.serviceFeeType;
        if (serviceFeeType === 'Fixed Fee') {
          return 'Enter fixed Fee';
        }
        return 'Enter percentage Fee';
      },
    },
    // Delivery Boy Settings Section

    // Peak Hours Section
    {
      name: 'peakHoursSection',
      type: 'section',
      sectionTitle: 'Peak Hours (Add upto 5 peak hours for per day)',
      sectionLine: true,
    },
    {
      name: 'peakHoursStatus',
      label: 'Peak Hours Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'peakHoursData',
      label: 'Peak Hours Configuration',
      type: 'peakHours',
      isRequired: true,
      dependsOn: { field: 'peakHoursStatus', value: 'Enable' },
    },

    // Order and Cart Settings Section
    /*{
            name: 'orderCartSection',
            type: 'section',
            sectionTitle: 'Order and Cart Settings',
            sectionLine: true,
        },
        {
            name: 'orderHistoryExpire',
            label: 'Order History Expire',
            type: 'radio',
            isRequired: true,
            radioOptions: ['Enable', 'Disable'],
        },
        {
            name: 'orderHistoryDuration',
            label: 'Order History Duration',
            type: 'text',
            isRequired: true,
            placeholder: 'Enter days',
            dependsOn: { field: 'orderHistoryExpire', value: 'Enable' },
        },
        {
            name: 'abandonedCartMailStatus',
            label: 'Abandoned Cart Mail Status',
            type: 'radio',
            isRequired: true,
            radioOptions: ['Enable', 'Disable'],
        },
        {
            name: 'sendMailAfter',
            label: 'Send Mail After',
            type: 'text',
            isRequired: true,
            placeholder: 'Enter hours',
            dependsOn: { field: 'abandonedCartMailStatus', value: 'Enable' },
        },*/

    // Referral and Security Section
    {
      name: 'referralSecuritySection',
      type: 'section',
      sectionTitle: 'Referral',
      sectionLine: true,
    },
    {
      name: 'referFriends',
      label: 'Refer Friends',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'offerPercentage',
      label: 'Offer Percentage',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter offer percentage',
      dependsOn: { field: 'referFriends', value: 'Enable' },
    },
    {
      name: 'maxReferralAmount',
      label: 'Max Referral Amount',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter max referral amount',
      dependsOn: { field: 'referFriends', value: 'Enable' },
    },
    // {
    //     name: 'passwordProtection',
    //     label: 'Password protection',
    //     type: 'radio',
    //     isRequired: true,
    //     radioOptions: ['Enable', 'Disable'],
    // },
    // {
    //     name: 'showCaptcha',
    //     label: 'Show captcha',
    //     type: 'radio',
    //     isRequired: true,
    //     radioOptions: ['Enable', 'Disable'],
    // },
    // {
    //     name: 'invalidLoginSuspend',
    //     label: 'Invalid login suspend (for customer)',
    //     type: 'radio',
    //     isRequired: true,
    //     radioOptions: ['Enable', 'Disable'],
    // },

    // Restaurant Settings Section
    {
      name: 'restaurantSection',
      type: 'section',
      sectionTitle: 'Store Settings',
      sectionLine: true,
    },
    {
      name: 'featuredRestaurant',
      label: 'Featured Stores',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Enable', 'Disable'],
    },
    {
      name: 'featuredPrice',
      label: 'Featured Price (per day)',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter featured price',
      dependsOn: { field: 'featuredRestaurant', value: 'Enable' },
    },
    {
      name: 'commonCommission',
      label: 'Common commission',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter common commission',
    },

    // Currency and Time Settings Section
    {
      name: 'currencyTimeSection',
      type: 'section',
      sectionTitle: 'Currency and Time Settings',
      sectionLine: true,
    },
    // {
    //     name: 'apiKeyForCurrencyConverter',
    //     label: 'API key for currency converter (It\'s only applicable for non JOD)',
    //     type: 'text',
    //     isRequired: true,
    //     placeholder: 'Enter API key for currency converter',
    // },
    /*{
            name: 'timePeriodForPreorder',
            label: 'Time period for Preparing for Delivery of Pre order.',
            type: 'text',
            isRequired: true,
            placeholder: 'Enter time period in minutes',
        },*/
    {
      name: 'timeZone',
      label: 'Time Zone',
      type: 'select',
      isRequired: true,
      options: [
        {
          value: '(GMT+14:00) Pacific/Kiritimati',
          label: '(GMT+14:00) Pacific/Kiritimati',
        },
        {
          value: '(GMT+13:00) Pacific/Apia',
          label: '(GMT+13:00) Pacific/Apia',
        },
        {
          value: '(GMT+12:00) Pacific/Auckland',
          label: '(GMT+12:00) Pacific/Auckland',
        },
        {
          value: '(GMT+11:00) Pacific/Guadalcanal',
          label: '(GMT+11:00) Pacific/Guadalcanal',
        },
        {
          value: '(GMT+10:00) Australia/Sydney',
          label: '(GMT+10:00) Australia/Sydney',
        },
        {
          value: '(GMT+9:30) Australia/Adelaide',
          label: '(GMT+9:30) Australia/Adelaide',
        },
        { value: '(GMT+9:00) Asia/Tokyo', label: '(GMT+9:00) Asia/Tokyo' },
        {
          value: '(GMT+8:00) Asia/Shanghai',
          label: '(GMT+8:00) Asia/Shanghai',
        },
        { value: '(GMT+7:00) Asia/Bangkok', label: '(GMT+7:00) Asia/Bangkok' },
        { value: '(GMT+6:30) Asia/Rangoon', label: '(GMT+6:30) Asia/Rangoon' },
        { value: '(GMT+6:00) Asia/Dhaka', label: '(GMT+6:00) Asia/Dhaka' },
        { value: '(GMT+5:30) Asia/Kolkata', label: '(GMT+5:30) Asia/Kolkata' },
        {
          value: '(GMT+5:00) Asia/Karachi (Pakistan Time)',
          label: '(GMT+5:00) Asia/Karachi (Pakistan Time)',
        },
        { value: '(GMT+4:30) Asia/Kabul', label: '(GMT+4:30) Asia/Kabul' },
        { value: '(GMT+4:00) Asia/Dubai', label: '(GMT+4:00) Asia/Dubai' },
        { value: '(GMT+3:30) Asia/Tehran', label: '(GMT+3:30) Asia/Tehran' },
        {
          value: '(GMT+3:00) Europe/Moscow',
          label: '(GMT+3:00) Europe/Moscow',
        },
        {
          value: '(GMT+2:00) Europe/Athens',
          label: '(GMT+2:00) Europe/Athens',
        },
        { value: '(GMT+1:00) Europe/Paris', label: '(GMT+1:00) Europe/Paris' },
        { value: '(GMT+0:00) UTC', label: '(GMT+0:00) UTC' },
        {
          value: '(GMT-1:00) Atlantic/Azores',
          label: '(GMT-1:00) Atlantic/Azores',
        },
        {
          value: '(GMT-2:00) Atlantic/South_Georgia',
          label: '(GMT-2:00) Atlantic/South_Georgia',
        },
        {
          value: '(GMT-3:00) America/Sao_Paulo',
          label: '(GMT-3:00) America/Sao_Paulo',
        },
        {
          value: '(GMT-4:00) America/Caracas',
          label: '(GMT-4:00) America/Caracas',
        },
        {
          value: '(GMT-5:00) America/New_York',
          label: '(GMT-5:00) America/New_York',
        },
        {
          value: '(GMT-6:00) America/Chicago',
          label: '(GMT-6:00) America/Chicago',
        },
        {
          value: '(GMT-7:00) America/Denver',
          label: '(GMT-7:00) America/Denver',
        },
        {
          value: '(GMT-8:00) America/Los_Angeles',
          label: '(GMT-8:00) America/Los_Angeles',
        },
        {
          value: '(GMT-9:00) America/Anchorage',
          label: '(GMT-9:00) America/Anchorage',
        },
        {
          value: '(GMT-10:00) Pacific/Honolulu',
          label: '(GMT-10:00) Pacific/Honolulu',
        },
        {
          value: '(GMT-11:00) Pacific/Midway',
          label: '(GMT-11:00) Pacific/Midway',
        },
        {
          value: '(GMT-12:00) Pacific/Kwajalein',
          label: '(GMT-12:00) Pacific/Kwajalein',
        },
      ],
    },
    {
      name: 'dateTimeFormat',
      label: 'Date Time Format',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter date time format',
    },

    // Loyalty Rewards Section
    {
      name: 'loyaltySection',
      type: 'section',
      sectionTitle: 'Loyalty Rewards',
      sectionLine: true,
    },
    {
      name: 'loyaltyRewardsStatus',
      label: 'Loyalty Rewards Status',
      type: 'radio',
      isRequired: true,
      radioOptions: ['Active', 'Deactive'],
    },
    {
      name: 'minimumTargetPoints',
      label: 'Minimum Target Points',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter minimum target points',
      dependsOn: { field: 'loyaltyRewardsStatus', value: 'Active' },
    },
    {
      name: 'usdOneEquals',
      label: 'currency To Points Ratio',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter points per JOD',
      dependsOn: { field: 'loyaltyRewardsStatus', value: 'Active' },
    },
    {
      name: 'redeemPointValue',
      label: 'Redeem point value',
      type: 'text',
      isRequired: true,
      placeholder: 'Enter redeem point value',
      dependsOn: { field: 'loyaltyRewardsStatus', value: 'Active' },
    },
  ];

  const handleSubmit = (values: Record<string, any>) => {
    handleGeneralSettingsSubmit(values as FormValues);
  };

  const buildTouchedState = (errorObj: any): any => {
    if (Array.isArray(errorObj)) {
      return errorObj.map((item) =>
        typeof item === 'object' && item !== null
          ? buildTouchedState(item)
          : true
      );
    }

    if (errorObj && typeof errorObj === 'object') {
      return Object.keys(errorObj).reduce(
        (acc, key) => {
          acc[key] = buildTouchedState(errorObj[key]);
          return acc;
        },
        {} as Record<string, any>
      );
    }

    return true;
  };

  const formConfig = {
    initialValues: formValues,
    validationSchema,
    onSubmit: handleSubmit,
    fields: allFields,
    validateOnChange: true, // Enable real-time validation
    validateOnBlur: true, // Enable validation on blur
    enableReinitialize: true, // Enable reinitialization when formValues change
    customButton: ({
      handleSubmit,
      validateForm,
      setTouched,
      isSubmitting,
    }: any) => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
        <Button
          type="button"
          variant="contained"
          sx={{
            backgroundColor: COLORS.primary.main,
            textTransform: 'none',
            '&:hover': { backgroundColor: COLORS.primary.dark },
          }}
          disabled={isSubmitting || isSaving}
          onClick={async () => {
            console.log('values', formConfig.initialValues);
            const validationErrors = await validateForm();
            console.log('validationErrors', validationErrors);
            if (Object.keys(validationErrors).length > 0) {
              if (setTouched) {
                setTouched(buildTouchedState(validationErrors), false);
              }
              toast.error(
                'Please resolve the highlighted errors before saving.'
              );
              return;
            }
            handleSubmit();
          }}
        >
          {isSaving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </Box>
    ),
  };

  // Debug: Check if form config is valid
  if (!formConfig.fields || formConfig.fields.length === 0) {
    return (
      <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
        <Typography variant="h4">General Settings</Typography>
        <Typography>No fields to display</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 600,
          fontSize: 24,
          marginBottom: 3,
          fontFamily: 'Livvic',
        }}
      >
        General Settings
      </Typography>
      {allForm(formConfig)()}
    </Box>
  );
};

export default GeneralSettings;
